import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Slider,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Grid,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import {
  CoTravelerResult,
  CoTravelerEncounter,
  PathfinderDataPoint,
  pathfinderApi
} from '../../services/pathfinderApi';

const COTRAVELER_POINTS_PER_BATCH = 100;

type CoTravelerBackendResponse = {
  items: Array<{ id: string; count: number }>;
  matches: Array<{
    entity_id?: string;
    device_id?: string;
    entity_type?: string;
    timestamp: string;
    coordinates: [number, number];
    area_index: number;
    distance_m: number;
  }>;
  _debug?: { pol_points: number; search_areas: number };

};

interface EnhancedCoTravelerAnalysisProps {
  dataPoints: PathfinderDataPoint[]; // The LOADED data points from Pathfinder
  selectedStudents: string[];
  selectedDevices: Record<string, string[]>;
  coTravelerResults?: CoTravelerResult[]; // Current co-traveler results
  onVisualizationUpdate?: (results: CoTravelerResult[]) => void;
  coTravelerVisibility?: Record<string, boolean>; // Map of co-traveler device ID to visibility state
  onToggleCoTravelerVisibility?: (resultKey: string, visible: boolean) => void; // Handler for toggling co-traveler visibility
  onZoomToLocation?: (lat: number, lon: number, zoom?: number) => void; // Handler for zooming to a specific location on the map
  onLoadPolForDevice?: (deviceId: string) => void;
}

function getLocationName(coords: [number, number]): string {
  return `Location ${coords[0].toFixed(4)}, ${coords[1].toFixed(4)}`;
}

function normalizeId(id: string): string {
  return (id || '').replace(/^GN-/i, '').replace(/-(mobile|phone)$/i, '');
}

const EnhancedCoTravelerAnalysis: React.FC<EnhancedCoTravelerAnalysisProps> = ({
  dataPoints, // The loaded data points from Pathfinder
  selectedStudents,
  selectedDevices,
  coTravelerResults = [],
  onVisualizationUpdate,
  coTravelerVisibility = {},
  onToggleCoTravelerVisibility,
  onZoomToLocation,
  onLoadPolForDevice,
}) => {

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // single device selection for analysis
const [selectedPrimaryDevice, setSelectedPrimaryDevice] = useState<string | null>(null);

  // Analysis parameters
  const [params, setParams] = useState({
    proximityThreshold: 100, // meters
    timeThreshold: 5, // minutes (±5 minutes before/after)
    minEncounters: 2,
    minDuration: 5, // minutes
  });

    // Get all available device IDs from loaded data (deduplicated)
  const allAvailableDevices = useMemo(() => {
    const raw = selectedStudents.flatMap(entityId => 
      selectedDevices[entityId] || []
    );
    // Deduplicate: strip "GN-" prefix and device-type suffix to find duplicates
    // Keep the first occurrence (prefer non-GN version)
    const seen = new Set<string>();
    return raw.filter(deviceId => {
      const baseId = normalizeId(deviceId);
      if (seen.has(baseId)) return false;
      seen.add(baseId);
      return true;
    });
  }, [selectedStudents, selectedDevices]);

  const primaryDevicePoints = useMemo(() => {
    if (!selectedPrimaryDevice) return [];
    const norm = (id: string) => (id || '').replace(/^GN-/i, '').replace(/-(mobile|phone)$/i, '');
    const primaryNorm = norm(selectedPrimaryDevice);
    const sorted = dataPoints
      .filter((p) => norm(p.deviceId || p.studentId || '') === primaryNorm)
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    const GRID = 0.0006; // ~60m, matches backend grid
    const TIME_BUCKET = 300_000; // 5 min in ms, matches backend
    const seen = new Set<string>();
    return sorted.filter((p) => {
      const latB = (Math.round(p.coordinates[0] / GRID) * GRID).toFixed(4);
      const lonB = (Math.round(p.coordinates[1] / GRID) * GRID).toFixed(4);
      const tB = Math.floor(p.timestamp.getTime() / TIME_BUCKET);
      const key = `${tB}_${latB}_${lonB}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [dataPoints, selectedPrimaryDevice]);

  const hasPolForPrimaryDevice = primaryDevicePoints.length > 0;

// Helper function to convert backend response to CoTravelerResult format
  const buildCoTravelerResults = useCallback((
    responses: CoTravelerBackendResponse[],
    primaryDeviceId: string
  ): CoTravelerResult[] => {
    const allMatches = responses.flatMap((r) => r.matches || []);
    const byEntity = new Map<string, typeof allMatches>();
    for (const m of allMatches) {
      const entityId = m.entity_id || m.device_id || 'Unknown';
      if (!byEntity.has(entityId)) byEntity.set(entityId, []);
      byEntity.get(entityId)!.push(m);
    }

    const results: CoTravelerResult[] = [];
    for (const [coTravelerDeviceId, matches] of Array.from(byEntity.entries())) {      const dedupeKey = (m: (typeof allMatches)[0]) =>
        `${m.timestamp}_${(m.coordinates?.[0] ?? 0).toFixed(5)}_${(m.coordinates?.[1] ?? 0).toFixed(5)}`;
      const seen = new Set<string>();
      const unique = matches.filter((m) => {
        const k = dedupeKey(m);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });

      if (unique.length < params.minEncounters) continue;
      const timestamps = unique.map((m) => new Date(m.timestamp).getTime()).sort((a, b) => a - b);
      const spanMinutes = (timestamps[timestamps.length - 1] - timestamps[0]) / 60_000;
      if (spanMinutes < params.minDuration) continue;

      const encounters: CoTravelerEncounter[] = unique.map((match) => ({
        timestamp: new Date(match.timestamp),
        location: match.coordinates,
        duration: params.timeThreshold,
        proximity: match.distance_m ?? 0,
        confidence: Math.max(0, 1 - (match.distance_m ?? 0) / params.proximityThreshold),
        activity: undefined,
        locationName: getLocationName(match.coordinates),
      }));

      const totalDuration = encounters.reduce((s, e) => s + e.duration, 0);
      const averageProximity = encounters.reduce((s, e) => s + e.proximity, 0) / encounters.length;
      const confidenceScore = encounters.reduce((s, e) => s + e.confidence, 0) / encounters.length;

      const locationGroups = new Map<string, { location: [number, number]; name?: string; encounters: number; totalDuration: number }>();
      for (const e of encounters) {
        const gridKey = `${e.location[0].toFixed(3)},${e.location[1].toFixed(3)}`;
        if (!locationGroups.has(gridKey)) {
          locationGroups.set(gridKey, { location: e.location, name: e.locationName, encounters: 0, totalDuration: 0 });
        }
        const g = locationGroups.get(gridKey)!;
        g.encounters++;
        g.totalDuration += e.duration;
      }

      results.push({
        primaryDevice: primaryDeviceId,
        coTravelerDevice: coTravelerDeviceId,
        encounters,
        totalEncounters: encounters.length,
        totalDuration,
        averageProximity,
        confidenceScore,
        temporalOverlap: 0,
        spatialOverlap: 0,
        frequentLocations: Array.from(locationGroups.values()).sort((a, b) => b.encounters - a.encounters).slice(0, 10),
      });
    }
    return results.sort((a, b) => b.confidenceScore - a.confidenceScore);
  }, [params.timeThreshold, params.proximityThreshold, params.minEncounters, params.minDuration]);

  const cancelledRef = React.useRef(false);

    const handleStartAnalysis = useCallback(async () => {
    if (!selectedPrimaryDevice) {
      alert('Please select a primary device to analyze co-travelers for');
      return;
    }
    if (primaryDevicePoints.length === 0) {
      alert('No POL data on the map for the selected device. Load POL for this device (or switch to a tab that has it), then run analysis.');
      return;
    }

    cancelledRef.current = false;
    setIsAnalyzing(true);
    setProgress(0);
    setLoadingMessage('Starting co-traveler analysis...');

    const POINTS_PER_BATCH = COTRAVELER_POINTS_PER_BATCH;
    const batches: PathfinderDataPoint[][] = [];
    for (let i = 0; i < primaryDevicePoints.length; i += POINTS_PER_BATCH) {
      batches.push(primaryDevicePoints.slice(i, i + POINTS_PER_BATCH));
    }
    const totalBatches = batches.length;

    console.log('[CoTrav] Plan (by points):', {
      totalBatches,
      pointsPerBatch: POINTS_PER_BATCH,
      totalPoints: primaryDevicePoints.length,
    });
    setLoadingMessage(
      totalBatches === 1
        ? 'Running co-traveler analysis (this may take a few minutes)...'
        : `Running co-traveler analysis (${totalBatches} batches of up to ${POINTS_PER_BATCH} points)...`
    );

    try {
      const payloadBase = {
        device_id: selectedPrimaryDevice,
        radius_meters: params.proximityThreshold,
        time_threshold_minutes: params.timeThreshold,
        min_encounters: params.minEncounters,
        min_duration_minutes: params.minDuration,
      };
      const MAX_CONCURRENT = 2;
      const windowResponses: CoTravelerBackendResponse[] = new Array(batches.length);
      let nextIndex = 0;
      let completed = 0;

      const runBatch = async (batchIndex: number): Promise<void> => {
        const batch = batches[batchIndex];
        console.log(`[CoTrav] Request batch ${batchIndex + 1}/${totalBatches}:`, {
          points: batch.length,
        });
        const resp = await pathfinderApi.client.post<CoTravelerBackendResponse>(
          '/pathfinder/co-travelers/analyze',
          {
            ...payloadBase,
            points: batch.map((p) => ({
              lat: p.coordinates[0],
              lon: p.coordinates[1],
              ts: p.timestamp.toISOString(),
            })),
          },
          { timeout: 300000 }
        );
        windowResponses[batchIndex] = resp.data;
        const { items = [], matches = [], _debug } = resp.data;
        console.log(`[CoTrav] Response batch ${batchIndex + 1}/${totalBatches}:`, {
          coTravelers: items.length,
          matches: matches.length,
          ...(_debug && { polPoints: _debug.pol_points, searchAreas: _debug.search_areas }),
        });
        completed++;
        setProgress(Math.round((completed / totalBatches) * 100));
        setLoadingMessage(`Analyzed ${completed} of ${totalBatches} batches...`);
      };

      const runNext = async (): Promise<void> => {
        while (nextIndex < batches.length) {
          if (cancelledRef.current) return;
          const idx = nextIndex++;
          await runBatch(idx);
        }
      };

      const workers = Array.from(
        { length: Math.min(MAX_CONCURRENT, batches.length) },
        () => runNext()
      );
      await Promise.all(workers);

      if (cancelledRef.current) {
        setLoadingMessage('Analysis cancelled.');
        return;
      }
      const results = buildCoTravelerResults(
        windowResponses.filter(Boolean),
        selectedPrimaryDevice
      );

      console.log('[CoTrav] Merged results:', {
        totalCoTravelers: results.length,
        totalEncounters: results.reduce((sum, r) => sum + r.encounters.length, 0),
        batchCount: totalBatches,
      });

      await onVisualizationUpdate?.(results);
      setLoadingMessage(
        results.length > 0
          ? `Found ${results.length} co-traveler(s)!`
          : 'No co-travelers found.'
      );
      setTimeout(() => setLoadingMessage(''), 3000);
    } catch (error: any) {
      alert(`Analysis failed: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsAnalyzing(false);
      setProgress(0);
    }
  }, [selectedPrimaryDevice, primaryDevicePoints, params, onVisualizationUpdate, buildCoTravelerResults]);

  const handleStopAnalysis = useCallback(() => {
    cancelledRef.current = true;
    setIsAnalyzing(false);
    setProgress(0);
  }, []);

  

  const handleExportResults = useCallback(() => {
    if (coTravelerResults.length === 0) return;

    const csvData = coTravelerResults.map(result => ({
      'Primary Device': result.primaryDevice,
      'Co-Traveler Device': result.coTravelerDevice,
      'Total Encounters': result.totalEncounters,
      'Total Duration (min)': result.totalDuration.toFixed(1),
      'Average Proximity (m)': result.averageProximity.toFixed(1),
      'Confidence Score': (result.confidenceScore * 100).toFixed(1) + '%',
      'Temporal Overlap': result.temporalOverlap.toFixed(1) + '%',
      'Spatial Overlap': result.spatialOverlap.toFixed(1) + '%',
      'Frequent Locations': result.frequentLocations.length,
    }));

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `co-traveler-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [coTravelerResults]);

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes.toFixed(1)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins.toFixed(0)}m`;
  };

  const formatDistance = (meters: number) => {
    if (meters < 1000) return `${meters.toFixed(0)}m`;
    return `${(meters / 1000).toFixed(2)}km`;
  };

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <GroupIcon color="primary" sx={{ fontSize: 18 }} />
        Enhanced Co-Traveler Analysis
      </Typography>
      
      <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ mb: 0.25 }}>Co‑Traveler Analysis </Typography>
        <Typography variant="caption" fontSize="0.7rem">
          Using the loaded OSINT API data, searches co-travelers in the same timeframe and geofence.
        </Typography>
      </Alert>

      {/* Analysis Parameters */}
      <Card sx={{ mb: 1.5 }}>
        <CardHeader 
          title="Analysis Parameters"
          titleTypographyProps={{ variant: 'subtitle2', fontWeight: 'bold' }}
          action={
            <Tooltip title="Configure analysis settings">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
          sx={{ py: 1 }}
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>Proximity Threshold: {params.proximityThreshold}m</Typography>
              <Slider
                size="small"
                value={params.proximityThreshold}
                onChange={(_, value) => setParams(prev => ({ ...prev, proximityThreshold: value as number }))}
                min={10}
                max={500}
                step={10}
                marks={[
                  { value: 50, label: '50m' },
                  { value: 100, label: '100m' },
                  { value: 200, label: '200m' },
                ]}
                disabled={isAnalyzing} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>Time Threshold: ±{params.timeThreshold} minutes</Typography>
              <Slider
                size="small"
                value={params.timeThreshold}
                onChange={(_, value) => setParams(prev => ({ ...prev, timeThreshold: value as number }))}
                min={1}
                max={60}
                step={1}
                marks={[
                  { value: 5, label: '5min' },
                  { value: 15, label: '15min' },
                  { value: 30, label: '30min' }
                ]}
                disabled={isAnalyzing}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography gutterBottom>Min Encounters: {params.minEncounters}</Typography>
              <Slider
                size="small"
                value={params.minEncounters}
                onChange={(_, value) => setParams(prev => ({ ...prev, minEncounters: value as number }))}
                min={1}
                max={10}
                step={1}
                disabled={isAnalyzing}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography gutterBottom>Min Duration: {params.minDuration} minutes</Typography>
              <Slider
                size="small"
                value={params.minDuration}
                onChange={(_, value) => setParams(prev => ({ ...prev, minDuration: value as number }))}
                min={1}
                max={60}
                step={1}
                disabled={isAnalyzing}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <CardContent sx={{ pt: 0 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Primary Device
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedPrimaryDevice || ''}
                onChange={(e) => setSelectedPrimaryDevice(e.target.value ? e.target.value : null)}
                disabled={isAnalyzing}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em>Select a device</em>
                </MenuItem>
                {allAvailableDevices.map(deviceId => (
                  <MenuItem key={deviceId} value={deviceId}>
                    {deviceId.length > 50 
                      ? deviceId.substring(0, 25) + '...' + deviceId.substring(deviceId.length - 25)
                      : deviceId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Select one device to find co-travelers throughout its entire pattern of life.
            </Typography>
          </CardContent>
        </Box>

      {/* Control Buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          onClick={() => selectedPrimaryDevice && onLoadPolForDevice?.(selectedPrimaryDevice)}
          disabled={isAnalyzing || !selectedPrimaryDevice}
          size="small"
          title="Load Pattern of Life for the selected device onto the map"
        >
          Load POL
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartAnalysis}
          disabled={isAnalyzing || !selectedPrimaryDevice || !hasPolForPrimaryDevice}
          startIcon={<PlayIcon />}
          size="small"
          title={!hasPolForPrimaryDevice && selectedPrimaryDevice ? 'Load POL for the selected device (or switch to a tab that has it) to run analysis' : undefined}
        >
          {isAnalyzing ? 'Analyzing…' : 'Start Analysis'}
        </Button>
        <Button
          variant="outlined"
          onClick={handleStopAnalysis}
          disabled={!isAnalyzing}
          startIcon={<StopIcon />}
          size="small"
        >
          Stop
        </Button>

        <Button
          variant="outlined"
          onClick={handleExportResults}
          disabled={coTravelerResults.length === 0}
          startIcon={<DownloadIcon />}
          size="small"
        >
          Export CSV
        </Button>

        <Button
          variant="outlined"
          onClick={() => onVisualizationUpdate?.([])}
          disabled={coTravelerResults.length === 0}
          startIcon={<RefreshIcon />}
          size="small"
        >
          Clear
        </Button>

               <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={selectedPrimaryDevice ? '1 primary device' : 'No device selected'} 
            size="small" 
            variant="outlined"
            color={selectedPrimaryDevice ? 'primary' : 'default'}
          />
          <Chip 
            label={`${coTravelerResults.length} co-travelers found`} 
            size="small" 
            color={coTravelerResults.length > 0 ? 'success' : 'default'}
          />
        </Box>
      </Box>

      {/* Progress Bar */}
      {isAnalyzing && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress 
            variant={progress > 0 && progress < 100 ? "determinate" : "indeterminate"} 
            value={progress > 0 ? progress : undefined} 
          />
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
            {loadingMessage || 'Analyzing co-traveler patterns...'} {progress > 0 ? `${Math.round(progress)}%` : ''}
          </Typography>
        </Box>
      )}

      {/* Analysis Status */}
      {dataPoints.length === 0 && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption">No GPS data loaded! Please load data in the tool first, then run co-traveler analysis.</Typography>
        </Alert>
      )}
      
            {dataPoints.length > 0 && !selectedPrimaryDevice && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption">{dataPoints.length} GPS points loaded. Please select a device to analyze for co-travelers.</Typography>
        </Alert>
      )}
      
      {dataPoints.length > 0 && selectedPrimaryDevice && !hasPolForPrimaryDevice && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption">
            No POL data on the map for this device. Use &quot;Load POL&quot; here or load data from another tab, then run analysis.
          </Typography>
        </Alert>
      )}
      {dataPoints.length > 0 && selectedPrimaryDevice && hasPolForPrimaryDevice && (
        <Alert severity="success" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption">
            Ready! {primaryDevicePoints.length} GPS points for {selectedPrimaryDevice}. Batches of up to {COTRAVELER_POINTS_PER_BATCH} points will be analyzed.
          </Typography>
        </Alert>
      )}

      {/* Co-traveler results are now integrated into the left sidebar device tree */}
      {coTravelerResults.length > 0 && (
        <Alert severity="success" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption">
            ✅ Co-traveler analysis complete! Results are now displayed under each device in the left panel.
            Use the toggles in the device tree to control visibility on the map.
          </Typography>
        </Alert>
      )}
      
      {/* Detailed Results Accordion */}
      {coTravelerResults.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {/* Select All / Deselect All controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={coTravelerResults.every(r => {
                    const key = `${r.primaryDevice}-${r.coTravelerDevice}`;
                    return coTravelerVisibility[key] !== false;
                  })}
                  indeterminate={
                    coTravelerResults.some(r => {
                      const key = `${r.primaryDevice}-${r.coTravelerDevice}`;
                      return coTravelerVisibility[key] !== false;
                    }) &&
                    !coTravelerResults.every(r => {
                      const key = `${r.primaryDevice}-${r.coTravelerDevice}`;
                      return coTravelerVisibility[key] !== false;
                    })
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    coTravelerResults.forEach(r => {
                      const key = `${r.primaryDevice}-${r.coTravelerDevice}`;
                      onToggleCoTravelerVisibility?.(key, checked);
                    });
                  }}
                  size="small"
                />
              }
              label={
                <Typography variant="caption" fontWeight="bold">
                  Select All / Deselect All
                </Typography>
              }
            />
            <Typography variant="caption" color="text.secondary">
              ({coTravelerResults.filter(r => coTravelerVisibility[`${r.primaryDevice}-${r.coTravelerDevice}`] !== false).length} of {coTravelerResults.length} shown on map)
            </Typography>
          </Box>

          {coTravelerResults.map((result, _) => {
            const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
            const isVisible = coTravelerVisibility[resultKey] !== false;
            
            return (
              <Accordion key={resultKey} sx={{ mb: 1, opacity: isVisible ? 1 : 0.5 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Checkbox
                      checked={isVisible}
                      onChange={(e) => {
                        e.stopPropagation();
                        onToggleCoTravelerVisibility?.(resultKey, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{ p: 0, mr: -1 }}
                    />
                    <Typography variant="subtitle2">
                      {result.primaryDevice} ↔ {result.coTravelerDevice}
                    </Typography>
                    <Chip 
                      label={`${result.totalEncounters} encounters`} 
                      size="small" 
                      color="primary" 
                    />
                    <Chip 
                      label={`${(result.confidenceScore * 100).toFixed(0)}% confidence`} 
                      size="small" 
                      color={getConfidenceColor(result.confidenceScore)}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Encounter Timeline
                      </Typography>
                      <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {result.encounters.map((encounter, idx) => (
                          <Box 
                            key={idx} 
                            sx={{ 
                              mb: 1, 
                              p: 1, 
                              bgcolor: 'grey.50', 
                              borderRadius: 1,
                              cursor: onZoomToLocation ? 'pointer' : 'default',
                              '&:hover': onZoomToLocation ? {
                                bgcolor: 'grey.100',
                                transform: 'translateX(2px)',
                                transition: 'all 0.2s'
                              } : {}
                            }}
                            onClick={() => {
                              if (onZoomToLocation) {
                                onZoomToLocation(encounter.location[0], encounter.location[1], 28);
                              }
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'black'}}>
                              {encounter.timestamp.toLocaleString()}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'black' }}>
                              {formatDistance(encounter.proximity)} • {formatDuration(encounter.duration)}
                              {encounter.locationName && (
                                <Typography variant="caption" sx={{ color: 'black' }} display="block">
                                  {encounter.locationName}
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Frequent Locations
                      </Typography>
                      <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {result.frequentLocations.map((location, idx) => (
                          <Box 
                            key={idx} 
                            sx={{ 
                              mb: 1, 
                              p: 1, 
                              bgcolor: 'grey.50', 
                              borderRadius: 1,
                              cursor: onZoomToLocation ? 'pointer' : 'default',
                              '&:hover': onZoomToLocation ? {
                                bgcolor: 'grey.100',
                                transform: 'translateX(2px)',
                                transition: 'all 0.2s'
                              } : {}
                            }}
                            onClick={() => {
                              if (onZoomToLocation) {
                                onZoomToLocation(location.location[0], location.location[1], 15);
                              }
                            }}
                          >
                            <Typography variant="body2" sx={{ color: 'black' }}>
                              {location.name || `Location ${idx + 1}`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'black' }}>
                              {location.encounters} encounters • {formatDuration(location.totalDuration)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default EnhancedCoTravelerAnalysis;
